import {Fragment, useEffect, useState} from "react";
import {get, getResponseBody, getResponseStatus, handleApiError, handleExpiredEntryLinkError, post} from "../js/api";
import {useHistory} from "react-router";
import {markDirty, showError, showMessage} from "../js/main";
import {BreadCrumb} from "./Breadcrumb";
import {Button} from "./form/Button";
import {OrderInformation} from "./OrderInformation";
import {getOwnUser} from "../controller/Profile";
import {useTanConfirmation} from "../js/useTanConfirmation";
import {TanValidity} from "./TanValidity";
import {InputCheckbox} from "./form/InputCheckbox";
import {isValidTan, sanitizeTan, TanInput} from "./form/TanInput";


export const UpdateOrderComponent = ({identifier, token = false, disableCancel = false, initialTan = ""}) => {

    const prefix = token ? '/public' : '';
    const [selectedOrder, setSelectedOrder] = useState();

    const [submitting, setSubmitting] = useState(false);
    const [canCancel, setCanCancel] = useState(true);
    const [canSign, setCanSign] = useState(false);

    const {signatureCode, setSignatureCode, tanStatus, tanBusy, requestNewTan, handleTanError} = useTanConfirmation({
        path: `${prefix}/orders/${identifier}`,
        enabled: selectedOrder?.state === "CREATED" && canSign,
        submitting,
        initialTan
    });

    const [confirmWiderrufsrecht, setConfirmWiderrufsrecht] = useState(false);

    useEffect(() => {
        setCanSign(false);
        setSelectedOrder(undefined);
        get(`${prefix}/orders/${identifier}`, false).then((data) => {
            setSelectedOrder(data);
        }).catch(err => {
            if (!handleExpiredEntryLinkError(err)) {
                handleApiError(err);
            }
        })

    }, [identifier, prefix]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(!selectedOrder){
            return;
        }
        if(!token){
            getOwnUser().then((user) => {
                if(selectedOrder.username && selectedOrder.username.toLowerCase() === user.username.toLowerCase()){
                    setCanSign(true);
                    setCanCancel(["CREATED", "AWAIT_PAYMENT"].indexOf(selectedOrder.state) !== -1 && !disableCancel);
                }else{
                    setCanSign(false);
                    setCanCancel(false);
                }
            });
        } else {
            setCanSign(true);
            setCanCancel(["CREATED", "AWAIT_PAYMENT"].indexOf(selectedOrder.state) !== -1 && !disableCancel);
        }
    },[selectedOrder, token, disableCancel])

    const history = useHistory();

    const signOrder = () => {
        if (submitting || tanBusy || !tanStatus) return;
        if (!confirmWiderrufsrecht) {
            showError("Bitte bestätigen Sie den Entfall des Widerrufsrechts nach Produktionsbeginn.");
            return;
        }
        const tan = sanitizeTan(signatureCode);
        setSignatureCode(tan);
        if (!isValidTan(tan)) {
            showError("Bitte geben Sie den 6-stelligen Code ein.");
            return;
        }

        setSubmitting(true);

        const orderIdentifier = token ? identifier : selectedOrder.id;
        const req = {
            tan
        };

        post(`${prefix}/orders/${orderIdentifier}/signOrder`, req, false).then((resp) => {
            markDirty();
            let order;
            let msg = "Die Bestellung wurde erfolgreich bestätigt."
            if(resp.paymentLink){
                order = resp.orderInfo;
                msg += " Sie werden in 5 Sekunden auf die Bezahlseite weitergeleitet.";
                const url = new URL(resp.paymentLink);
                setTimeout(() => history.push(url.pathname), 5000);
            }else{
                order = resp;
            }
            showMessage(msg);
            if(!token){
                history.push("/meine-bestellungen");
            }else{
                setSelectedOrder(order);
            }
        }).catch(async err => {
            if (handleExpiredEntryLinkError(err)) {
                return;
            }
            if (await handleTanError(err)) return;
            setSignatureCode("");
            let msg = "Der eingegebene Code war nicht korrekt.";
            if (getResponseStatus(err) === 422) {
                switch (getResponseBody(err)?.errorType) {
                    case "RETRY":
                        msg += " Bitte versuchen Sie es erneut.";
                        break;
                    case "FAIL":
                        msg += " Die Bestellung wurde storniert.";
                        history.push("/meine-bestellungen");
                        break;
                    default:
                        break;
                }
                showError(msg);
                return;
            }
            handleApiError(err);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const cancelOrder = () => {
        setSubmitting(true);
        const orderIdentifier = token ? identifier : selectedOrder.id;
        post(`${prefix}/orders/${orderIdentifier}/cancelOrder`, null, false).then(() => {
            markDirty();
            showMessage("Die Bestellung wurde erfolgreich storniert.");
            history.push("/bestellungen");
        }).catch(err => {
            if (!handleExpiredEntryLinkError(err)) {
                handleApiError(err);
            }
        }).finally(() => {
            setSubmitting(false);
        });
    };

    return (
        <Fragment>
            {selectedOrder &&
                <div>
                    <BreadCrumb currentTitle={"Bestellung " + selectedOrder.id} parentTitle={token ? '' : "Meine Bestellungen"} parentLink={token ? '' : "/meine-bestellungen"}/>

                    {selectedOrder.state === "CREATED" &&
                        <div className="zeichnung mt-5 mb-5">
                            <h2 className="primary-link mt-0">Bestellung autorisieren</h2>
                            {canSign && <>
                                <p>
                                    <strong>Sie haben den Bestätigungscode per Mail erhalten. Überprüfen Sie Ihren
                                        Spam-Ordner!</strong> Mit Eingabe des
                                    Codes wird die Bestellung abgeschickt, Sie stimmen damit unseren AGB zu und es wird ein
                                    rechtsgültiges Geschäft
                                    abgeschlossen. Der Code besteht nur aus Zahlen.<br/>
                                </p>
                                <form onSubmit={event => {
                                    event.preventDefault();
                                    signOrder();
                                }}>
                                    <div>
                                        <h4>Entfall Widerrufsrecht nach Produktionsbeginn</h4>
                                        <p>
                                            Eine nachträgliche Änderung oder Stornierung ist nach Produktionsbeginn nicht mehr möglich.
                                            Ich bestätige ausdrücklich, dass mein Schlüssel individuell nach meinen Angaben angefertigt wird und nehme zur Kenntnis, dass dadurch mein Widerrufsrecht entfällt.
                                        </p>
                                        <InputCheckbox value={confirmWiderrufsrecht} label={"Bestätigung Entfall Widerrufsrecht"} name={"widerrufsrecht"} required={true} setter={setConfirmWiderrufsrecht}/>
                                    </div>
                                    <TanInput value={signatureCode} setter={setSignatureCode}/>
                                    <TanValidity status={tanStatus} busy={tanBusy}/>
                                    <p>
                                        <small>
                                            <b className={"yellow"}>Ich bestätige hiermit,</b> dass ich berechtigt bin, den/die bestellen Schlüssel
                                            nachmachen zu lassen. <br/>
                                            Mir/uns ist bewusst, dass falsche Angaben rechtliche Schritte bzw. auch
                                            strafrechtliche Konsequenzen nach sich ziehen können. Ich verpflichte
                                            mich jeden Schaden bzw. Nachteil zu ersetzen, der aus einer falschen Angabe
                                            bzw. aus falschen Beilagen bzw. aus falschen Informationen resultieren,
                                            entstehen. <br/>
                                            <b className={"yellow"}>Mit der Eingabe des Freigabe - Codes erkläre ich mich damit einverstanden,</b> dass die angegeben Daten, inkl. möglicher Dokument-Kopien sowie der übermittelten Fotos im Sinne der DSGVO zur Bestellung eines Schlüssels herangezogen werden und dieser zur Anfertigung des/der Schlüssel/s verwendet werden oder an den Erzeuger zur Bestellung weitergegeben werden.
                                        </small>
                                    </p>
                                    <Button submitting={submitting || tanBusy || !tanStatus} width={220} theme="green mr-1">Bestellung
                                        bestätigen</Button>
                                    <Button submitting={submitting || tanBusy} width={230} type="button"
                                            onClick={() => requestNewTan()}
                                            theme="orange">Neuen Code anfordern</Button>
                                </form>
                            </>}
                            {!canSign && <>
                                <p>
                                    <strong>Die Bestellung kann nur vom Ersteller bestätigt werden.</strong>
                                </p>
                            </>}

                        </div>}

                    <OrderInformation order={selectedOrder} showUser/>

                    {canCancel && <form onSubmit={e => {
                        e.preventDefault();
                    }} className="storno mt-3 ">
                        <h3 className="red mt-0">Bestellung stornieren</h3>
                        <p>
                            Wenn Sie die Bestellung stornieren, kann das nicht mehr widerrufen werden. Wenn Sie die
                            Bestellung später dennoch
                            durchführen wollen, muss eine neue Bestellung erstellt werden. Nach der Zeichnung kann die
                            Bestellung nur mehr von
                            Mitarbeitern der Lintner GmbH storniert werden. Setzen Sie sich hierfür bei Bedarf mit dem
                            Kundenservice in
                            Kontakt.
                        </p>
                        <Button submitting={submitting} width={230} theme="red" onClick={() => cancelOrder()}>Bestellung
                            stornieren</Button>
                    </form>}

                    <br/><br/>
                </div>}
        </Fragment>
    );

};
